import type { Error as ApiError } from "~/api/Api";

export type StatusErrorLike = {
  status?: number;
  response?: {
    status?: number;
  };
};

type Success<T> = {
  data: T;
  error: null;
};

type Failure<E> = {
  data: null;
  error: E;
};

type Result<T, E> = Success<T> | Failure<E>;

// Generic transport-level tryCatch.
// Does NOT attempt to parse HTTP Response.
// Intended for promises that already throw typed errors.
export async function tryCatch<T, E = StatusErrorLike>(
  promise: Promise<T>,
): Promise<Result<T, E>> {
  try {
    const data = await promise;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: error as E };
  }
}

/**
 * HTTP-aware tryCatch.
 *
 * Difference from basic tryCatch:
 * - If a fetch Response is thrown (e.g. 422 from swagger client),
 *   it automatically parses response.json()
 *   and returns backend ApiError instead of raw Response.
 *
 * Use this for swagger-generated API methods.
 */
export async function tryCatchParsed<T>(
  promise: Promise<T>,
): Promise<Result<T, ApiError>> {
  try {
    const data = await promise;

    return { data, error: null };
  } catch (error) {
    if (error instanceof Response) {
      const json = await error.json();

      return { data: null, error: json.error as ApiError };
    }

    throw error; // unexpected transport error
  }
}
